import { customerSettingsFragment, permissionFragment, userFragment } from 'apollo/fragments';
import { connectedLabFragment, deviationTypesFragment, workFlowFragment } from 'apollo/fragments/organization';
import { dentistFragment, technicianFragment } from 'apollo/fragments/user';
import { gql } from 'graphql.macro';

export const getUserTemplate = gql`
    query user {
        user {
            ...userFields
            impersonatedBy
            impersonatedByEmail
            organization {
                _id
                name
                type
                activeActionSet
                officialName
                isTransportEnable
                isApiEnabled
                isWhiteLabeled
                notificationListDefaults {
                    showOnlyUnread
                    showNotDone
                }
                generalNotifications {
                    newOrder
                    newMessage
                    orderModified
                    orderResend
                    orderCancelled
                    orderRated
                }
                externalGeneralNotifications {
                    newInternalMessage
                    internalOrderModified
                    orderModified
                    orderDeclined
                    assignExtTech
                }
                customerSettings {
                    ...customerSettingsFields
                }
                transportSettings {
                    denthubTransport {
                        isEnabled
                        externalId
                    }
                    nShiftTransport {
                        isEnabled
                        partners {
                            id
                            description
                            services {
                                id
                                description
                                addons
                            }
                        }
                        waybillPackageSizes {
                            _id
                            name
                            width
                            height
                            length
                        }
                    }
                }
                defaultActionPriceListId
                defaultMaterialPriceListId
                organizationNumber
                connectedLabs {
                    ...connectedLabFields
                }
                invoicing {
                    configuration {
                        invoiceNumberType
                    }
                    communication {
                        email
                    }
                }
                contact {
                    address {
                        street
                        postalCode
                        city
                        country
                    }
                }
                workFlow {
                    ...workFlowFields
                }
                deviationTypes {
                    ...deviationTypesFields
                }
                unavailableLabDates
                holidayDays
                openingHours {
                    day
                    time {
                        from
                        to
                    }
                    active
                }
                activeActionSet
                actionSets {
                    _id
                    title
                    currency
                }
            }
            permissions {
                ...permissionFields
            }
        }
    }
    ${userFragment}
    ${permissionFragment}
    ${customerSettingsFragment}
    ${connectedLabFragment}
    ${workFlowFragment}
    ${deviationTypesFragment}
`;

export const getUserByEmailTemplate = gql`
    query userByEmail($email: String!) {
        userByEmail(email: $email) {
            _id
            organization {
                _id
                name
                isWhiteLabeled
                customerSettings {
                    ...customerSettingsFields
                }
            }
            email
            fullName
            role
            active
            isShared
            type
            whiteLabelAccess
            enableUserSync
        }
    }
    ${customerSettingsFragment}
`;

export const getDentistsTemplate = gql`
    query dentists($clinicId: MongoDbObjectId!, $filters: UsersFilters, $excludeLocalLabOwner: Boolean) {
        dentists(clinicId: $clinicId, filters: $filters, excludeLocalLabOwner: $excludeLocalLabOwner) {
            _id
            fullName
            name {
                firstName
                lastName
            }
        }
    }
`;

export const getDentistsForUserListTemplate = gql`
    query dentists($clinicId: MongoDbObjectId, $filters: UsersFilters, $limit: Int!, $offset: Int!) {
        dentists(clinicId: $clinicId, filters: $filters, limit: $limit, offset: $offset) {
            ...dentistFields
        }
    }
    ${dentistFragment}
`;

export const getDentistsForOrderTemplate = gql`
    query dentists($clinicId: MongoDbObjectId, $filters: UsersFilters) {
        dentists(clinicId: $clinicId, filters: $filters) {
            _id
            fullName
            email
            syncid
            name {
                firstName
                lastName
            }
        }
    }
`;

export const getTechniciansForUserListTemplate = gql`
    query technicians($filters: UsersFilters, $limit: Int!, $offset: Int!) {
        technicians(filters: $filters, limit: $limit, offset: $offset) {
            ...technicianFields
            salesforceUser {
                id
                name
                email
            }
        }
    }
    ${technicianFragment}
`;

export const getRolesTemplate = gql`
    query roles($type: String) {
        roles(type: $type) {
            _id
            role
        }
    }
`;

export const getChildClinicsWithParentTemplate = gql`
    query childClinicsWithParent($parentClinicId: MongoDbObjectId, $showOnlyDeliveryLocations: Boolean) {
        childClinicsWithParent(parentClinicId: $parentClinicId, showOnlyDeliveryLocations: $showOnlyDeliveryLocations) {
            _id
            type
            name
            isMain
            parentClinicId
            contact {
                communication {
                    email
                }
            }
        }
    }
`;

export const getLabsTemplate = gql`
    query labs($isTransportEnable: Boolean) {
        labs(isTransportEnable: $isTransportEnable) {
            _id
            name
        }
    }
`;

export const getLabsForUserListTemplate = gql`
    query labs($isTransportEnable: Boolean, $limit: Int, $offset: Int) {
        labs(isTransportEnable: $isTransportEnable, limit: $limit, offset: $offset) {
            _id
            name
            organizationNumber
            contact {
                address {
                    street
                    postalCode
                    city
                }
            }
            type
        }
    }
`;

export const getExternalTechnicians = gql`
    query technicians {
        # DD-3391: "external" matches every external tier, so the list keeps working
        # as tiers are added rather than silently dropping the new ones.
        technicians(filters: { active: true, role: "external" }) {
            _id
            email
            fullName
            name {
                firstName
                lastName
            }
        }
    }
`;

export const getDentistsForLinked = gql`
    query dentists($filters: UsersFilters!, $clinicId: MongoDbObjectId!) {
        dentists(clinicId: $clinicId, filters: $filters, limit: 75) {
            _id
            email
            fullName
            name {
                firstName
                lastName
            }
        }
    }
`;

export const getDeliveryClinicsTemplate = gql`
    query childClinicsWithParent($parentClinicId: MongoDbObjectId, $showOnlyDeliveryLocations: Boolean) {
        childClinicsWithParent(parentClinicId: $parentClinicId, showOnlyDeliveryLocations: $showOnlyDeliveryLocations) {
            _id
            name
            contact {
                address {
                    street
                    postalCode
                    city
                    country
                }
            }
        }
    }
`;

export const getDentistsWithClinicTemplate = gql`
    query dentistsWithClinic($filters: UsersFilters, $limit: Int!) {
        dentistsWithClinic(filters: $filters, limit: $limit) {
            _id
            fullName
            name {
                firstName
                lastName
            }
            email
            clinic {
                _id
                name
                local
                parentClinicId
            }
            fullNameWithClinicInfo
            dentistType
            local
            dentistLocalIds {
                lab
                localId
            }
            labDentistDefaultClinics {
                mainClinic
                defaultClinic {
                    _id
                    name
                }
            }
        }
    }
`;

export const getLabDentistsSearchTemplate = gql`
    query labDentistSearch($filters: LabDentistFilters) {
        labDentistSearch(filters: $filters) {
            _id
            fullName
            email
            dentistType
            local
            fullNameWithClinicInfo
            clinics {
                _id
                name
                local
            }
            dentistLocalIds {
                lab
                localId
            }
        }
    }
`;

export const getUserByIdTemplate = gql`
    query userById($userId: MongoDbObjectId!) {
        userById(userId: $userId) {
            _id
            name {
                firstName
                lastName
            }
            organization {
                _id
                name
            }
            client {
                _id
                name
            }
            phone
            email
            fullName
            active
            local
            dentistNotes {
                lab
                clinic
                note
            }
            dentistLocalIds {
                lab
                clinic
                localId
            }
            labDentistDefaultClinics {
                mainClinic
                defaultClinic {
                    _id
                    name
                }
            }
            linkedOrganizations {
                organization {
                    _id
                    name
                    active
                    connectedLabs {
                        lab {
                            _id
                        }
                    }
                }
            }
        }
    }
`;

export const isLoggedInTemplate = gql`
    query isLoggedIn {
        isLoggedIn
    }
`;

export const dentistsWithClinicForPrepopulateTemplate = gql`
    query dentistsWithClinicForPrepopulate($dentistName: String!, $clinicName: String!) {
        dentistsWithClinicForPrepopulate(dentistName: $dentistName, clinicName: $clinicName) {
            _id
            fullName
            name {
                firstName
                lastName
            }
            email
            clinic {
                _id
                name
                local
                parentClinicId
                isMain
            }
            fullNameWithClinicInfo
            dentistType
            local
            labDentistDefaultClinics {
                mainClinic
                defaultClinic {
                    _id
                    name
                }
            }
        }
    }
`;

export const getTechniciansForDepartmentsTemplate = gql`
    query technicians($filters: UsersFilters, $limit: Int!, $offset: Int!) {
        technicians(filters: $filters, limit: $limit, offset: $offset) {
            _id
            fullName
            name {
                firstName
                lastName
            }
        }
    }
`;
